import React, { useRef, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Slider,
  TextField,
  Popover,
  Paper,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Check as CheckIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  TextFields as TextIcon,
  Brush as BrushIcon,
  CropFree as RectangleIcon,
  RadioButtonUnchecked as CircleIcon,
  ArrowForward as ArrowIcon,
  Highlight as HighlightIcon,
  Colorize as ColorizeIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { Stage, Layer, Image as KonvaImage, Line, Rect, Circle, Arrow, Text, Transformer } from 'react-konva';
import useImage from 'use-image';

interface PhotoEditModalProps {
  open: boolean;
  onClose: () => void;
  imageSrc: string;
  onSave: (editedImageDataUrl: string, file: File) => void;
}

type ToolType = 'select' | 'draw' | 'text' | 'rectangle' | 'circle' | 'arrow' | 'highlight';

interface DrawingShape {
  id: string;
  type: 'line' | 'rect' | 'circle' | 'arrow' | 'text';
  points?: number[];
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  radius?: number;
  text?: string;
  fontSize?: number;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  pointerLength?: number;
  pointerWidth?: number;
}

const PhotoEditModal: React.FC<PhotoEditModalProps> = ({
  open,
  onClose,
  imageSrc,
  onSave
}) => {
  const stageRef = useRef<any>(null);
  const transformerRef = useRef<any>(null);
  const [image, imageStatus] = useImage(imageSrc);
  const [tool, setTool] = useState<ToolType>('select');
  const [color, setColor] = useState('#ff0000');
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [shapes, setShapes] = useState<DrawingShape[]>([]);
  const [selectedShapeId, setSelectedShapeId] = useState<string | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentShape, setCurrentShape] = useState<DrawingShape | null>(null);
  const [history, setHistory] = useState<DrawingShape[][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [textInput, setTextInput] = useState('');
  const [textPosition, setTextPosition] = useState<{ x: number; y: number } | null>(null);
  const [colorPickerAnchor, setColorPickerAnchor] = useState<HTMLElement | null>(null);

  const colors = [
    '#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff',
    '#000000', '#ffffff', '#ffa500', '#800080', '#ffc0cb', '#a52a2a'
  ];

  useEffect(() => {
    if (open) {
      setShapes([]);
      setHistory([]);
      setHistoryIndex(-1);
      setSelectedShapeId(null);
      setTool('select');
      setTextInput('');
      setTextPosition(null);
    }
  }, [open]);

  const addToHistory = (newShapes: DrawingShape[]) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push([...newShapes]);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setShapes([...history[newIndex]]);
      setSelectedShapeId(null);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setShapes([...history[newIndex]]);
      setSelectedShapeId(null);
    }
  };

  const handleDelete = () => {
    if (selectedShapeId) {
      const newShapes = shapes.filter(s => s.id !== selectedShapeId);
      setShapes(newShapes);
      addToHistory(newShapes);
      setSelectedShapeId(null);
    }
  };

  const handleClear = () => {
    setShapes([]);
    addToHistory([]);
    setSelectedShapeId(null);
  };

  const handleStageMouseDown = (e: any) => {
    if (tool === 'select') {
      const clickedOnEmpty = e.target === e.target.getStage();
      if (clickedOnEmpty) {
        setSelectedShapeId(null);
        return;
      }
      const id = e.target.id();
      setSelectedShapeId(id);
      return;
    }

    if (tool === 'text') {
      const stage = e.target.getStage();
      const point = stage.getPointerPosition();
      setTextPosition(point);
      return;
    }

    if (tool === 'draw' || tool === 'highlight') {
      const stage = e.target.getStage();
      const point = stage.getPointerPosition();
      setIsDrawing(true);
      const newShape: DrawingShape = {
        id: `line-${Date.now()}`,
        type: 'line',
        points: [point.x, point.y],
        stroke: tool === 'highlight' ? color : color,
        strokeWidth: tool === 'highlight' ? strokeWidth * 3 : strokeWidth,
        fill: tool === 'highlight' ? color + '40' : undefined
      };
      setCurrentShape(newShape);
      return;
    }

    if (tool === 'rectangle' || tool === 'circle' || tool === 'arrow') {
      const stage = e.target.getStage();
      const point = stage.getPointerPosition();
      setIsDrawing(true);
      const newShape: DrawingShape = {
        id: `${tool}-${Date.now()}`,
        type: tool === 'rectangle' ? 'rect' : tool === 'circle' ? 'circle' : 'arrow',
        x: point.x,
        y: point.y,
        width: 0,
        height: 0,
        radius: 0,
        stroke: color,
        strokeWidth: strokeWidth,
        fill: undefined,
        pointerLength: tool === 'arrow' ? 10 : undefined,
        pointerWidth: tool === 'arrow' ? 10 : undefined
      };
      setCurrentShape(newShape);
      return;
    }
  };

  const handleStageMouseMove = (e: any) => {
    if (!isDrawing || !currentShape) return;

    const stage = e.target.getStage();
    const point = stage.getPointerPosition();

    if (tool === 'draw' || tool === 'highlight') {
      const newPoints = currentShape.points!.concat([point.x, point.y]);
      setCurrentShape({ ...currentShape, points: newPoints });
    } else if (tool === 'rectangle' || tool === 'circle' || tool === 'arrow') {
      const width = point.x - currentShape.x!;
      const height = point.y - currentShape.y!;
      setCurrentShape({
        ...currentShape,
        width,
        height,
        radius: tool === 'circle' ? Math.abs(Math.min(width, height)) / 2 : undefined
      });
    }
  };

  const handleStageMouseUp = () => {
    if (isDrawing && currentShape) {
      const newShapes = [...shapes, currentShape];
      setShapes(newShapes);
      addToHistory(newShapes);
      setCurrentShape(null);
      setIsDrawing(false);
    }
  };

  const handleTextConfirm = () => {
    if (textInput && textPosition) {
      const newShape: DrawingShape = {
        id: `text-${Date.now()}`,
        type: 'text',
        x: textPosition.x,
        y: textPosition.y,
        text: textInput,
        fontSize: 20,
        fill: color
      };
      const newShapes = [...shapes, newShape];
      setShapes(newShapes);
      addToHistory(newShapes);
      setTextInput('');
      setTextPosition(null);
      setTool('select');
    }
  };

  const handleSave = () => {
    if (stageRef.current) {
      const dataURL = stageRef.current.toDataURL({ pixelRatio: 2 });
      
      // Convert data URL to File
      const byteString = atob(dataURL.split(',')[1]);
      const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: mimeString });
      const file = new File([blob], `edited_photo_${Date.now()}.jpg`, { type: 'image/jpeg' });

      onSave(dataURL, file);
      onClose();
    }
  };

  const stageWidth = 800;
  const stageHeight = 600;
  const imageScale = image
    ? Math.min(stageWidth / image.width, stageHeight / image.height, 1)
    : 1;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Éditer la photo</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Toolbar */}
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              {/* Tool Selection */}
              <ToggleButtonGroup
                value={tool}
                exclusive
                onChange={(_, value) => value && setTool(value)}
                size="small"
              >
                <ToggleButton value="select">
                  <Tooltip title="Sélectionner">
                    <EditIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="draw">
                  <Tooltip title="Dessiner">
                    <BrushIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="text">
                  <Tooltip title="Texte">
                    <TextIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="rectangle">
                  <Tooltip title="Rectangle">
                    <RectangleIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="circle">
                  <Tooltip title="Cercle">
                    <CircleIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="arrow">
                  <Tooltip title="Flèche">
                    <ArrowIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="highlight">
                  <Tooltip title="Surligner">
                    <HighlightIcon />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>

              <Divider orientation="vertical" flexItem />

              {/* Color Picker */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title="Couleur">
                  <IconButton
                    onClick={(e) => setColorPickerAnchor(e.currentTarget)}
                    sx={{ bgcolor: color, width: 40, height: 40, border: '2px solid', borderColor: 'divider' }}
                  >
                    <ColorizeIcon sx={{ color: 'white' }} />
                  </IconButton>
                </Tooltip>
                <Popover
                  open={Boolean(colorPickerAnchor)}
                  anchorEl={colorPickerAnchor}
                  onClose={() => setColorPickerAnchor(null)}
                >
                  <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, width: 200 }}>
                      {colors.map((c) => (
                        <Box
                          key={c}
                          onClick={() => {
                            setColor(c);
                            setColorPickerAnchor(null);
                          }}
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: c,
                            border: color === c ? '3px solid #2196f3' : '1px solid #ccc',
                            cursor: 'pointer',
                            borderRadius: 1
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Popover>
              </Box>

              {/* Stroke Width */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 150 }}>
                <Typography variant="caption">Épaisseur</Typography>
                <Slider
                  value={strokeWidth}
                  onChange={(_, value) => setStrokeWidth(value as number)}
                  min={1}
                  max={20}
                  size="small"
                  sx={{ width: 100 }}
                />
              </Box>

              <Divider orientation="vertical" flexItem />

              {/* Actions */}
              <Tooltip title="Annuler">
                <span>
                  <IconButton onClick={handleUndo} disabled={historyIndex <= 0}>
                    <UndoIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Refaire">
                <span>
                  <IconButton onClick={handleRedo} disabled={historyIndex >= history.length - 1}>
                    <RedoIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Supprimer">
                <span>
                  <IconButton onClick={handleDelete} disabled={!selectedShapeId}>
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Effacer tout">
                <IconButton onClick={handleClear}>
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>

          {/* Canvas */}
          <Box sx={{ display: 'flex', justifyContent: 'center', bgcolor: '#f5f5f5', p: 2, borderRadius: 2, minHeight: stageHeight }}>
            {imageStatus === 'loading' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 4 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Chargement de l'image...
                </Typography>
              </Box>
            )}
            {imageStatus === 'loaded' && image && (
              <Stage
                ref={stageRef}
                width={stageWidth}
                height={stageHeight}
                onMouseDown={handleStageMouseDown}
                onMouseMove={handleStageMouseMove}
                onMouseUp={handleStageMouseUp}
                onTouchStart={handleStageMouseDown}
                onTouchMove={handleStageMouseMove}
                onTouchEnd={handleStageMouseUp}
              >
                <Layer>
                  <KonvaImage
                    image={image}
                    x={(stageWidth - image.width * imageScale) / 2}
                    y={(stageHeight - image.height * imageScale) / 2}
                    width={image.width * imageScale}
                    height={image.height * imageScale}
                  />
                {shapes.map((shape) => {
                  if (shape.type === 'line') {
                    return (
                      <Line
                        key={shape.id}
                        id={shape.id}
                        points={shape.points}
                        stroke={shape.stroke}
                        strokeWidth={shape.strokeWidth}
                        fill={shape.fill}
                        tension={0.5}
                        lineCap="round"
                        lineJoin="round"
                        onClick={() => setSelectedShapeId(shape.id)}
                      />
                    );
                  }
                  if (shape.type === 'rect') {
                    return (
                      <Rect
                        key={shape.id}
                        id={shape.id}
                        x={shape.x}
                        y={shape.y}
                        width={shape.width}
                        height={shape.height}
                        stroke={shape.stroke}
                        strokeWidth={shape.strokeWidth}
                        fill={shape.fill}
                        onClick={() => setSelectedShapeId(shape.id)}
                      />
                    );
                  }
                  if (shape.type === 'circle') {
                    return (
                      <Circle
                        key={shape.id}
                        id={shape.id}
                        x={(shape.x || 0) + (shape.width || 0) / 2}
                        y={(shape.y || 0) + (shape.height || 0) / 2}
                        radius={shape.radius}
                        stroke={shape.stroke}
                        strokeWidth={shape.strokeWidth}
                        fill={shape.fill}
                        onClick={() => setSelectedShapeId(shape.id)}
                      />
                    );
                  }
                  if (shape.type === 'arrow') {
                    return (
                      <Arrow
                        key={shape.id}
                        id={shape.id}
                        x={shape.x}
                        y={shape.y}
                        points={[0, 0, shape.width || 0, shape.height || 0]}
                        stroke={shape.stroke}
                        strokeWidth={shape.strokeWidth}
                        fill={shape.stroke}
                        pointerLength={shape.pointerLength}
                        pointerWidth={shape.pointerWidth}
                        onClick={() => setSelectedShapeId(shape.id)}
                      />
                    );
                  }
                  if (shape.type === 'text') {
                    return (
                      <Text
                        key={shape.id}
                        id={shape.id}
                        x={shape.x}
                        y={shape.y}
                        text={shape.text}
                        fontSize={shape.fontSize}
                        fill={shape.fill}
                        onClick={() => setSelectedShapeId(shape.id)}
                      />
                    );
                  }
                  return null;
                })}
                {currentShape && (
                  <>
                    {currentShape.type === 'line' && (
                      <Line
                        points={currentShape.points}
                        stroke={currentShape.stroke}
                        strokeWidth={currentShape.strokeWidth}
                        fill={currentShape.fill}
                        tension={0.5}
                        lineCap="round"
                        lineJoin="round"
                      />
                    )}
                    {currentShape.type === 'rect' && (
                      <Rect
                        x={currentShape.x}
                        y={currentShape.y}
                        width={currentShape.width}
                        height={currentShape.height}
                        stroke={currentShape.stroke}
                        strokeWidth={currentShape.strokeWidth}
                        fill={currentShape.fill}
                      />
                    )}
                    {currentShape.type === 'circle' && (
                      <Circle
                        x={(currentShape.x || 0) + (currentShape.width || 0) / 2}
                        y={(currentShape.y || 0) + (currentShape.height || 0) / 2}
                        radius={currentShape.radius}
                        stroke={currentShape.stroke}
                        strokeWidth={currentShape.strokeWidth}
                        fill={currentShape.fill}
                      />
                    )}
                    {currentShape.type === 'arrow' && (
                      <Arrow
                        x={currentShape.x}
                        y={currentShape.y}
                        points={[0, 0, currentShape.width || 0, currentShape.height || 0]}
                        stroke={currentShape.stroke}
                        strokeWidth={currentShape.strokeWidth}
                        fill={currentShape.stroke}
                        pointerLength={currentShape.pointerWidth}
                        pointerWidth={currentShape.pointerWidth}
                      />
                    )}
                  </>
                )}
              </Layer>
            </Stage>
            )}
            {imageStatus === 'failed' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 4 }}>
                <Typography variant="body2" color="error">
                  Erreur lors du chargement de l'image
                </Typography>
              </Box>
            )}
          </Box>

          {/* Text Input Dialog */}
          {textPosition && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Entrer le texte
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder="Texte..."
                  size="small"
                  fullWidth
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleTextConfirm();
                    }
                  }}
                />
                <Button onClick={handleTextConfirm} variant="contained" startIcon={<CheckIcon />}>
                  Ajouter
                </Button>
                <Button onClick={() => {
                  setTextPosition(null);
                  setTextInput('');
                  setTool('select');
                }} variant="outlined">
                  Annuler
                </Button>
              </Box>
            </Paper>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Annuler
        </Button>
        <Button onClick={handleSave} variant="contained" startIcon={<CheckIcon />}>
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhotoEditModal;

